/**
 * Reusable "busy button" helper.
 *
 * Wrap an async action so the triggering button shows an inline circular spinner
 * and is disabled while the action runs — then restored (label + enabled state)
 * whether it resolves or throws. A re-entrancy guard ignores extra clicks while
 * the action is in flight, so slow requests can't be double-submitted.
 *
 * Usage:
 *   import { withButtonBusy } from '../../src/lib/button-busy.js';
 *   btn.addEventListener('click', () => withButtonBusy(btn, () => doAsyncThing()));
 *
 * The spinner + `.is-busy` styles live in admin.css (`.btn-spinner`).
 */
export async function withButtonBusy(btn, action, opts = {}) {
  if (typeof action !== 'function') return undefined;
  if (!btn) return action();
  if (btn.dataset.busy === '1') return undefined;   // already running — ignore repeat clicks

  btn.dataset.busy = '1';
  const prevHtml = btn.innerHTML;
  const prevDisabled = btn.disabled;
  const label = opts.busyLabel != null ? opts.busyLabel : (btn.textContent || '').trim();

  const spinner = document.createElement('span');
  spinner.className = 'btn-spinner';
  spinner.setAttribute('aria-hidden', 'true');

  btn.disabled = true;
  btn.classList.add('is-busy');
  btn.setAttribute('aria-busy', 'true');
  btn.replaceChildren(spinner);
  if (label) btn.append(' ' + label);

  try {
    return await action();
  } finally {
    btn.innerHTML = prevHtml;
    btn.disabled = prevDisabled;
    btn.classList.remove('is-busy');
    btn.removeAttribute('aria-busy');
    delete btn.dataset.busy;
  }
}

export default { withButtonBusy };

// Also expose for non-module callers (legacy inline handlers).
if (typeof window !== 'undefined') {
  window.uiBusy = Object.assign(window.uiBusy || {}, { withButtonBusy });
}
